// GiftPreviewContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

const SEND_BUTTON_LOADING_TIMEOUT_MS = 5000;

/**
 * Gift preview selection state. Panel shows when `hoveredGift ||
 * (selectedGift && isPreviewVisible)`; renders `hoveredGift ?? selectedGift`.
 */
const GiftPreviewContext = createContext(null);

/**
 * Returns the context value, or `null` when used outside a `GiftPreviewProvider`.
 * @returns {object|null}
 */
const useGiftPreview = () => useContext(GiftPreviewContext);

const GiftPreviewProvider = ({ children }) => {
  // selectedGift / hoveredGift shape: { id, image, pagImage, isPagGift, isAnimationGift }
  const [selectedGift, setSelectedGift] = useState(null);
  const [hoveredGift, setHoveredGift] = useState(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  // id of the gift whose PAG/APNG preview is still downloading
  const [loadingPreviewGiftId, setLoadingPreviewGiftId] = useState(null);
  // id of the gift whose preview download failed; shown as inline message
  const [previewErrorGiftId, setPreviewErrorGiftId] = useState(null);
  const loadingTimeoutRef = useRef(null);
  // Mirror of `loadingPreviewGiftId` for race-safe checks in callbacks —
  // a slow A failing after user switched to B is dropped, not staled into state.
  const loadingGiftIdRef = useRef(null);

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearLoadingTimeout, [clearLoadingTimeout]);

  const selectGift = useCallback((gift, { shouldShowPreview = true } = {}) => {
    setSelectedGift(gift);
    setIsPreviewVisible(shouldShowPreview);
    setHoveredGift(null);
  }, []);

  const hidePreview = useCallback(() => {
    setIsPreviewVisible(false);
    setHoveredGift(null);
  }, []);

  const hoverGift = useCallback(gift => {
    setHoveredGift(gift);
    // In Desktop, hover also trigger `isPreviewVisible` and hover-leave can revert the animation back to the selected gift.
    setIsPreviewVisible(true);
  }, []);

  const unhoverGift = useCallback(() => {
    setHoveredGift(null);
  }, []);

  const startPreviewLoading = useCallback(
    giftId => {
      if (!giftId) return;
      setLoadingPreviewGiftId(giftId);
      loadingGiftIdRef.current = giftId;
      // Re-attempt clears any previous error for this id; failure will re-set it.
      setPreviewErrorGiftId(current => (current === giftId ? null : current));
      // Watchdog gated on ref so a stale fire (user switched id) is a no-op.
      clearLoadingTimeout();
      loadingTimeoutRef.current = setTimeout(() => {
        loadingTimeoutRef.current = null;
        if (loadingGiftIdRef.current !== giftId) return;
        loadingGiftIdRef.current = null;
        setLoadingPreviewGiftId(current =>
          current === giftId ? null : current
        );
        // Inline failure overlay; late arrival clears via `endPreviewLoading`.
        setPreviewErrorGiftId(current => (current == null ? giftId : current));
      }, SEND_BUTTON_LOADING_TIMEOUT_MS);
    },
    [clearLoadingTimeout]
  );

  // Race-protected via `loadingGiftIdRef`: stale failure (user already moved
  // away) is dropped. Natural success also clears any prior timeout-error
  // for this id so a late-arriving canvas can replace the failure overlay.
  const endPreviewLoading = useCallback(
    (giftId, { hasError = false } = {}) => {
      const isActiveLoader = loadingGiftIdRef.current === giftId;
      if (isActiveLoader) {
        loadingGiftIdRef.current = null;
      }
      setLoadingPreviewGiftId(current => (current === giftId ? null : current));
      if (hasError) {
        if (isActiveLoader) setPreviewErrorGiftId(giftId);
      } else {
        setPreviewErrorGiftId(current => (current === giftId ? null : current));
      }
      clearLoadingTimeout();
    },
    [clearLoadingTimeout]
  );

  const value = useMemo(
    () => ({
      selectedGift,
      hoveredGift,
      isPreviewVisible,
      loadingPreviewGiftId,
      previewErrorGiftId,
      selectGift,
      hidePreview,
      hoverGift,
      unhoverGift,
      startPreviewLoading,
      endPreviewLoading,
    }),
    [
      selectedGift,
      hoveredGift,
      isPreviewVisible,
      loadingPreviewGiftId,
      previewErrorGiftId,
      selectGift,
      hidePreview,
      hoverGift,
      unhoverGift,
      startPreviewLoading,
      endPreviewLoading,
    ]
  );

  return (
    <GiftPreviewContext.Provider value={value}>
      {children}
    </GiftPreviewContext.Provider>
  );
};

GiftPreviewProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useGiftPreview, GiftPreviewProvider };
export default GiftPreviewContext;
